<script setup lang="ts">
import ModalComponent from '@/components/ModalComponent.vue'
import StyledButton from '@/components/StyledButton.vue'

interface Props {
	modelValue: boolean
	roleName?: string
	saving?: boolean
	saveFailed?: boolean
}
defineProps<Props>()

const emit = defineEmits<{
	keepEditing: []
	saveAndContinue: []
	discardAndContinue: []
}>()
</script>

<template>
	<ModalComponent
		:model-value="modelValue"
		@update:model-value="(show: boolean) => !show && !saving && emit('keepEditing')"
	>
		<template #modalBody>
			<div class="p-6">
				<div class="flex items-center mb-4">
					<div class="rounded-full p-2" :class="saveFailed ? 'bg-red-100' : 'bg-amber-100'">
						<span
							class="material-symbols-outlined"
							:class="saveFailed ? 'text-red-600' : 'text-amber-600'"
						>{{ saveFailed ? 'error' : 'save' }}</span>
					</div>
					<h2 class="ml-3 text-lg font-semibold text-gray-800">
						{{ saveFailed ? 'No se pudieron guardar los cambios' : 'Guarda los cambios antes de continuar' }}
					</h2>
				</div>

				<p v-if="!saveFailed" class="text-sm text-gray-700 mb-6">
					Modificaste los permisos del rol
					<strong>{{ roleName }}</strong> y aún no se guardan. Para continuar
					primero deben guardarse los cambios.
				</p>
				<p v-else class="text-sm text-gray-700 mb-6">
					No se pudieron guardar los permisos del rol
					<strong>{{ roleName }}</strong> (revisa el mensaje de error). Puedes
					reintentar el guardado, o descartar los cambios y continuar de todos
					modos.
				</p>

				<div class="flex justify-end gap-3">
					<StyledButton
						label="Seguir editando"
						class="bg-gray-200 text-gray-700 hover:bg-gray-300"
						type="button"
						:disabled="saving"
						@click="emit('keepEditing')"
					/>
					<StyledButton
						v-if="saveFailed"
						label="Descartar cambios y continuar"
						class="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
						type="button"
						:disabled="saving"
						@click="emit('discardAndContinue')"
					/>
					<StyledButton
						:label="saving ? 'Guardando…' : saveFailed ? 'Reintentar guardado' : 'Guardar y continuar'"
						class="bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-60"
						type="button"
						:disabled="saving"
						@click="emit('saveAndContinue')"
					/>
				</div>
			</div>
		</template>
	</ModalComponent>
</template>
